import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header.jsx';
import { EstadoBadge } from '../components/ui/EstadoBadge.jsx';
import { MultiComboboxSelect } from '../components/ui/MultiComboboxSelect.jsx';
import { DateRangePicker } from '../components/ui/DateRangePicker.jsx';
import { LeadForm } from '../features/leads/LeadForm.jsx';
import { useLeads, useCreateLead, useExportarLeads } from '../features/leads/useLeads.js';
import { useVendedores } from '../features/usuarios/useUsuarios.js';
import { useAuthStore } from '../stores/authStore.js';
import { TODOS_LOS_ESTADOS } from '../features/estados/useEstados.js';
import { formatFecha } from '../lib/formatters.js';

const ORIGENES = ['Presencial', 'Google', 'WhatsApp', 'Redes', 'Mail', 'Referido', 'Web', 'Informe'];

export default function LeadsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const usuario = useAuthStore((s) => s.usuario);
  const esAdmin = usuario?.rol === 'admin';
  const esViewer = usuario?.rol === 'viewer';
  const vendedores = useVendedores();
  const [modalAbierto, setModalAbierto] = useState(false);

  // Filtros derivados de la URL
  const nombre = searchParams.get('nombre') || '';
  const page = Number(searchParams.get('page')) || 1;
  const filters = {
    estado:      searchParams.getAll('estado'),
    origen:      searchParams.getAll('origen'),
    vendedor_id: searchParams.getAll('vendedor_id'),
    fecha_desde: searchParams.get('fecha_desde') || undefined,
    fecha_hasta: searchParams.get('fecha_hasta') || undefined,
  };

  const activeFilters = {
    nombre:      nombre.trim() || undefined,
    estado:      filters.estado.length      ? filters.estado      : undefined,
    origen:      filters.origen.length      ? filters.origen      : undefined,
    vendedor_id: filters.vendedor_id.length ? filters.vendedor_id : undefined,
    fecha_desde: filters.fecha_desde,
    fecha_hasta: filters.fecha_hasta,
    page,
  };

  const { data: resultado, isLoading } = useLeads(activeFilters);
  const leads      = Array.isArray(resultado?.data) ? resultado.data : Array.isArray(resultado) ? resultado : [];
  const totalPages = Number(resultado?.totalPages ?? 1);
  const total      = Number(resultado?.total      ?? 0);
  const crear = useCreateLead();
  const exportar = useExportarLeads();

  const hayFiltros = filters.estado.length || filters.origen.length || filters.vendedor_id.length || filters.fecha_desde || filters.fecha_hasta || nombre.trim();

  function setParam(key, value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(key);
      next.delete('page');
      if (Array.isArray(value)) {
        value.forEach((v) => next.append(key, v));
      } else if (value !== undefined && value !== null && value !== '') {
        next.set(key, value);
      }
      return next;
    });
  }

  function setPage(p) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (p === 1) next.delete('page'); else next.set('page', p);
      return next;
    });
  }

  function handleNombre(e) { setParam('nombre', e.target.value); }
  function handleFiltro(key, value) { setParam(key, value); }
  function handleFecha({ from, to }) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('fecha_desde');
      next.delete('fecha_hasta');
      next.delete('page');
      if (from) next.set('fecha_desde', from);
      if (to) next.set('fecha_hasta', to);
      return next;
    });
  }
  function handleLimpiar() { setSearchParams({}); }
  function handleCrear(data) { crear.mutate(data, { onSuccess: () => setModalAbierto(false) }); }
  // Se manda `activeFilters` tal cual: sin filtros el backend devuelve la base
  // completa, y con filtros exporta exactamente lo que se está viendo en la grilla.
  function handleExportar() { exportar.mutate(activeFilters); }

  return (
    <div>
      <Header title="Leads" />
      <div className="p-6 flex flex-col gap-4">

        {/* Barra de filtros + botón nuevo */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-48">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">Nombre / Teléfono</p>
            <input
              type="text"
              value={nombre}
              onChange={handleNombre}
              placeholder="Nombre o teléfono..."
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C]"
            />
          </div>

          {(esAdmin || esViewer) && (
            <div className="w-48">
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">Vendedor</p>
              <MultiComboboxSelect
                options={vendedores.map((v) => ({ value: v.id, label: v.nombre }))}
                value={filters.vendedor_id}
                onChange={(v) => handleFiltro('vendedor_id', v)}
                placeholder="Todos"
              />
            </div>
          )}

          <div className="w-48">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">Estado</p>
            <MultiComboboxSelect
              options={TODOS_LOS_ESTADOS.map((e) => ({ value: e, label: e }))}
              value={filters.estado}
              onChange={(v) => handleFiltro('estado', v)}
              placeholder="Todos"
            />
          </div>

          <div className="w-40">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">Origen</p>
            <MultiComboboxSelect
              options={ORIGENES.map((o) => ({ value: o, label: o }))}
              value={filters.origen}
              onChange={(v) => handleFiltro('origen', v)}
              placeholder="Todos"
            />
          </div>

          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1.5">Período</p>
            <DateRangePicker
              from={filters.fecha_desde}
              to={filters.fecha_hasta}
              onChange={handleFecha}
            />
          </div>

          {hayFiltros ? (
            <button
              onClick={handleLimpiar}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors self-end pb-2"
            >
              Limpiar filtros
            </button>
          ) : null}

          <div className="ml-auto flex items-end gap-3">
            <button
              onClick={handleExportar}
              disabled={exportar.isPending || total === 0}
              title={hayFiltros
                ? 'Descarga los leads que coinciden con los filtros aplicados'
                : 'Descarga todos los leads'}
              className="border-2 border-[#0D2B5C] text-[#0D2B5C] hover:bg-[#0D2B5C] hover:text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#0D2B5C]"
            >
              {exportar.isPending
                ? 'Generando...'
                : `Descargar Excel${hayFiltros ? ' (filtrado)' : ''}`}
            </button>

            {!esViewer && (
              <button
                onClick={() => setModalAbierto(true)}
                className="bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-6 py-3 rounded-md transition-colors min-h-[44px]"
              >
                + Nuevo lead
              </button>
            )}
          </div>
        </div>

        {exportar.error && (
          <p className="text-red-500 text-sm font-['Inter']">{exportar.error.message}</p>
        )}

        {/* Tabla */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-x-auto">
          {isLoading && !resultado ? (
            <p className="p-6 font-['Inter'] text-[#6B7280]">Cargando...</p>
          ) : leads.length === 0 ? (
            <p className="p-6 font-['Inter'] text-[#6B7280]">No hay leads con esos filtros.</p>
          ) : (
            <table className="w-full text-sm font-['Inter']">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Nombre</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Teléfono</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Estado</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Origen</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Observación</th>
                  <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Fecha</th>
                  <th className="text-center px-4 py-3 text-[#6B7280] font-medium">Verificado</th>
                  {(esAdmin || esViewer)
                    ? <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Vendedor</th>
                    : <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Abono</th>
                  }
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-[#0D2B5C] font-medium">{lead.nombre_apellido}</td>
                    <td className="px-4 py-3 text-[#6B7280]">{lead.telefono}</td>
                    <td className="px-4 py-3"><EstadoBadge estado={lead.estado} /></td>
                    <td className="px-4 py-3 text-[#6B7280]">{lead.origen}</td>
                    <td className="px-4 py-3 text-[#6B7280] max-w-[200px] truncate">{lead.observacion}</td>
                    <td className="px-4 py-3 text-[#6B7280]">{formatFecha(lead.fecha_ingreso)}</td>
                    <td className="px-4 py-3 text-center text-emerald-500">
                      {lead.verificado ? '✓' : ''}
                    </td>
                    {(esAdmin || esViewer)
                      ? <td className="px-4 py-3 text-[#6B7280]">{lead.vendedor?.nombre ?? '—'}</td>
                      : <td className="px-4 py-3 text-[#6B7280]">{lead.abono ?? '—'}</td>
                    }
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#6B7280] font-['Inter']">
              {total} leads · página {page} de {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm font-['Inter'] rounded-md border border-gray-200 text-[#6B7280] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                const mostrar = p === 1 || p === totalPages || Math.abs(p - page) <= 1;
                const esEllipsis = !mostrar && (p === 2 || p === totalPages - 1);
                if (esEllipsis) return <span key={p} className="px-1 text-[#6B7280] text-sm">…</span>;
                if (!mostrar) return null;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1.5 text-sm font-['Inter'] rounded-md border transition-colors ${
                      p === page
                        ? 'bg-[#0D2B5C] text-white border-[#0D2B5C]'
                        : 'border-gray-200 text-[#6B7280] hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm font-['Inter'] rounded-md border border-gray-200 text-[#6B7280] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal nuevo lead */}
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl mx-4">
            <h2 className="font-['Raleway'] font-bold text-[#0D2B5C] text-lg mb-5">Nuevo lead</h2>
            {crear.error && (
              <p className="text-red-500 text-sm font-['Inter'] mb-3">
                {crear.error?.response?.data?.error || 'Error al crear el lead'}
              </p>
            )}
            <LeadForm
              onSubmit={handleCrear}
              isPending={crear.isPending}
              onCancel={() => setModalAbierto(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
